import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Typography, Box, FlexBox, Button, Grid, Tabs, color, Tooltip } from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as authActions, selectors as authSelectors } from 'store/auth';
import { Client, selectors as clientsSelectors } from 'store/clients';
import { actions as venueActions } from 'store/venues';
import { getMatchPath } from 'utils/urlUtils';
import ActivityLogView from 'views/ActivityLogView';
import ConfigurationView from 'views/ConfigurationView';
import CustomersView from 'views/CustomersView';
import DeliveryLogView from 'views/DeliveryLogView';
import KiosksView from 'views/KiosksView';
import MediaCenterView from 'views/MediaCenterView';
import SettingsView from 'views/SettingsView';
import UsersView from 'views/UsersView';
import packageJSON from '../../package.json';
import { useStylesDashboard } from './styled';

// RETURNS THE PATHNAME WITHOUT THE KIOSKID
const pathInit = (path: string) => {
  const match = getMatchPath(path, '/kiosks/:kioskId?');

  // IF THERE IS A MATCH OF PATH AND THAT PATH HAS KIOSKID, SLICE IT OUT
  if (match && match.params.kioskId) return path.slice(0, path.lastIndexOf('/')).trim();
  return path;
};

const DashboardRoutes: React.FC = () => {
  const location = useLocation();

  const { logout } = useActions({ logout: authActions.logout });
  const { resetVenueAction } = useActions({ resetVenueAction: venueActions.resetAction });

  const admin = useSelector(authSelectors.selectAdmin);
  const activeClient = useSelector(clientsSelectors.selectClient);
  const client = useSelector(clientsSelectors.selectClient) as Client;

  const path = useMemo(() => pathInit(location.pathname), [location.pathname]);
  const [to, setTo] = useState<string>(path);

  const classes = useStylesDashboard();

  const globalAdmin = admin?.role === 'GlobalAdmin';
  const readOnlyAdmin = admin?.role === 'ReadOnlyAdmin';
  const activeClientName =
    activeClient?.name && activeClient.name.length > 20
      ? `${activeClient.name.slice(0, 20)}...`
      : (activeClient?.name ?? '');

  useEffect(() => {
    if (!path.includes(to)) setTo(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const onRouteChange = (event: React.MouseEvent<HTMLButtonElement>, v: string | number) => setTo(v.toString());

  const clearStoreAndLogout = () => {
    resetVenueAction();
    logout();
  };

  const openSupportLink = () => {
    window.open(`https://kistechnologies.atlassian.net/servicedesk/customer/portal/4`);
  };

  // IF NO CLIENT GO TO THE ROOT (SIGN IN) PAGE
  if (!client) return <Navigate to="/" />;

  return (
    <FlexBox bg="white" overflow="hidden" raised flexOverride="1" noWrap style={{ borderRadius: '32px 32px 0 0' }}>
      <div className={classes.dashboardSideTabs}>
        <FlexBox width={236} direction="column" justify="between" fullHeight borderRight={`1px solid ${color.BORDER}`}>
          <FlexBox direction="column" align="center">
            {activeClient && globalAdmin && (
              <Box padding="4 6">
                <Typography className={classes.activeCustomerText}>
                  Active customer:
                  <br />
                  <Tooltip title={activeClient.name} arrow placement="top" verticalOffset={12}>
                    <span>{activeClientName}</span>
                  </Tooltip>
                </Typography>
              </Box>
            )}
            <Tabs value={to} onChange={onRouteChange}>
              {globalAdmin && (
                <Tabs.Tab iconName="customer" iconViewBox="0 0 24 24" label="Customers" to="/customers" />
              )}
              <Tabs.Tab iconName="kiosks" label="Kiosks" to="/kiosks" />
              <Tabs.Tab iconName="credential" label="Users" to="/users" />
              <Tabs.Tab iconName="activity_log" iconViewBox="0 0 59 36" label="Activity Log" to="/activityLog" />
              <Tabs.Tab iconName="ticket" iconViewBox="-5 0 59 36" label="Delivery Log" to="/deliveryLog" />
              {!readOnlyAdmin && <Tabs.Tab iconName="media_center" label="Media Center" to="/mediaCenter" />}
              {!readOnlyAdmin && (
                <Tabs.Tab iconViewBox="0 0 24 24" iconName="settings" label="Settings" to="/settings" />
              )}
              {globalAdmin && (
                <Tabs.Tab iconViewBox="0 0 24 24" iconName="settings" label="Configuration" to="/configuration" />
              )}
            </Tabs>
          </FlexBox>
          <FlexBox direction="column" align="center" margin="0 0 5">
            <Box margin="4 0 6">
              <Button customSize="small" width={144} onClick={openSupportLink}>
                Support
              </Button>
            </Box>
            <Button customSize="small" width={144} onClick={clearStoreAndLogout}>
              Log Out
            </Button>
            <Box margin="4 0 0">
              <Typography fontSize={12} align="center">
                Version: {packageJSON.version}
              </Typography>
              <Typography fontSize={12} align="center">
                ©KIS Technologies
              </Typography>
            </Box>
          </FlexBox>
        </FlexBox>
      </div>
      <Grid className={classes.dashboardInnerContainer} direction="column">
        <Routes>
          <Route path="/users" element={<UsersView />} />
          <Route path="/activityLog" element={<ActivityLogView />} />
          <Route path="/mediaCenter" element={<MediaCenterView />} />
          <Route path="/deliveryLog" element={<DeliveryLogView />} />
          <Route path="/kiosks" element={<KiosksView />} />
          {globalAdmin && <Route path="/customers" element={<CustomersView />} />}
          {!readOnlyAdmin && <Route path="/settings" element={<SettingsView />} />}
          {globalAdmin && <Route path="/configuration" element={<ConfigurationView />} />}
        </Routes>
      </Grid>
    </FlexBox>
  );
};

export default DashboardRoutes;
